<template>
	<div
		:class="`relative ${pictureSize} overflow-hidden aspect-[${pictureAspectRatio}]`"
	>
		<NuxtLink
			v-if="product"
			class="cursor-pointer w-full h-full block"
			:to="routeToProduct"
			:class="linkStyles || ''"
			@click="handleClick"
		>
			<Favorite
				v-if="product.Id"
				:absolute="true"
				:wrapper-size="8"
				rounded="full"
				:product="product"
			/>
			<div class="flex items-center justify-center h-full w-full">
				<NuxtImg
					:src="
						product?.Files?.[0]?.Path
							? product.Files[0].Path + '?mode=max&format=webp&w=390'
							: defaultImage
					"
					:alt="product?.Name || 'Default product image'"
					class="max-h-full max-w-full w-full object-cover cursor-pointer"
					:loading="lazyLoading ? 'lazy' : 'eager'"
					:preload="!lazyLoading"
					sizes="20vw md:50vw"
					width="364"
					height="546"
					:provider="product?.Files?.[0]?.Path ? undefined : 'storyblok'"
				/>
			</div>
			<ApplicationFlag
				v-for="(flagId, index) in productFlags"
				:key="flagId"
				:data="getFlagData(flagId)"
				:position="getItemPosition(flagId, index)"
			/>
		</NuxtLink>
		<slot />
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { ApplicationFlag } from "~/types/storyblok/storyblok";

interface Props {
	product: Partial<FilteredProductItem> | ProductItem;
	pictureSize: string;
	pictureAspectRatio?: string;
	clickIsValid?: boolean;
	lazyLoading: boolean | undefined;
	linkStyles?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
	click: [];
}>();

const productStore = useProductStore();
const appStore = useAppStore();
const { productClick } = useGtmEvents();

const defaultImage = computed(() => {
	return productStore.defaultProductImage || "images/product/no_image.jpeg";
});

const routeToProduct = computed(() => {
	return `/product/${props.product?.UniqueName}`;
});

const productFlags = computed(() => {
	if (props.product?.FlagIdSeed) {
		return props.product.FlagIdSeed.split(",");
	}
	return [];
});

const { getItemPosition, getFlagData } = useApplicationFlags({
	appFlags: appStore.flags,
	productFlags: productFlags.value
});

const handleClick = () => {
	if (props.clickIsValid) {
		productClick(props.product);
		emit("click");
	}
};
</script>
